import { Platform } from 'react-native';
import { parseActivePlanGrant } from '../../../../../shared/billing-catalog';
import { supabase } from '../../shared/supabase/supabaseClient';
import type { PlanEntitlementsRow } from './domain/planEntitlements';
import { canRunOperationalBackgroundWork } from '../session/accountSetupService';
import {
  fetchMyOrganizationEntitlements,
  getOrganizationEntitlementsCacheIfFresh,
} from './planEntitlementsService';

export type PaidPlanCode = 'emprende' | 'crece';

const MARKETING_PLAN_SESSION_KEY = 'vuelto_marketing_plan_intent';

let planIntentClearedForSession = false;

/** Intención de plan antes de tener sesión (misma pestaña; no persiste cross-tab). */
let ephemeralPlanIntent: PaidPlanCode | null = null;

export function resetPlanIntentClearGuard(): void {
  planIntentClearedForSession = false;
}

export function setEphemeralPlanIntent(plan: PaidPlanCode | null): void {
  ephemeralPlanIntent = plan;
}

export function peekEphemeralPlanIntent(): PaidPlanCode | null {
  return ephemeralPlanIntent;
}

/** Intención de marketing visible en login (efímero o sessionStorage). */
export function peekMarketingPlanIntent(): PaidPlanCode | null {
  return ephemeralPlanIntent ?? readMarketingPlanSession() ?? planFromCurrentUrl();
}

export function planFromCurrentUrl(): PaidPlanCode | null {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return normalizePaidPlanCode(new URLSearchParams(window.location.search).get('plan'));
  }
  return null;
}

function writeMarketingPlanSession(plan: PaidPlanCode): void {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(MARKETING_PLAN_SESSION_KEY, plan);
  } catch {
    /* private mode / quota */
  }
}

function readMarketingPlanSession(): PaidPlanCode | null {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return null;
  try {
    return normalizePaidPlanCode(sessionStorage.getItem(MARKETING_PLAN_SESSION_KEY));
  } catch {
    return null;
  }
}

function clearMarketingPlanSession(): void {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(MARKETING_PLAN_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Intención comercial desde marketing / deep link.
 * Solo buffer en memoria + sessionStorage — **no** escribe pending en auth/org.
 * El pending durable se setea al iniciar checkout MP (`persistPlanIntent`).
 */
export function captureMarketingPlanIntent(planCode: PaidPlanCode): void {
  setEphemeralPlanIntent(planCode);
  writeMarketingPlanSession(planCode);
}

/**
 * Tras auth: recupera intención de URL / efímero / sessionStorage **sin** persistir pending.
 * Consume el buffer de marketing (una pasada).
 */
export async function flushEphemeralPlanIntentAfterAuth(): Promise<PaidPlanCode | null> {
  const fromUrl = planFromCurrentUrl();
  const fromSession = readMarketingPlanSession();
  const plan = fromUrl ?? ephemeralPlanIntent ?? fromSession;
  if (!plan) return null;
  setEphemeralPlanIntent(null);
  clearMarketingPlanSession();
  return plan;
}

/** Quita `plan` / `billing` de la URL para que un refresh no reabra Activar. */
export function clearPlanBillingParamsFromLocation(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of ['plan', 'billing'] as const) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (!changed) return;
    const qs = url.searchParams.toString();
    const next = `${url.pathname}${qs ? `?${qs}` : ''}${url.hash}`;
    window.history.replaceState({}, '', next);
  } catch {
    /* ignore */
  }
}

export function normalizePaidPlanCode(value: string | null | undefined): PaidPlanCode | null {
  const plan = (value ?? '').trim().toLowerCase();
  if (plan === 'emprende' || plan === 'crece') return plan;
  // Compatibilidad legacy
  if (plan === 'completo' || plan === 'esencial') return 'crece';
  return null;
}

export function isPaidPlanActive(planCode: string | null | undefined): boolean {
  const plan = normalizePaidPlanCode(planCode);
  return plan !== null;
}

/**
 * Persiste intención de plan en auth metadata y, si hay org de dueño, en
 * organizations.pending_subscription_plan.
 * Llamar **solo** al iniciar checkout (Mercado Pago / IAP handoff), no por deep link.
 */
export async function persistPlanIntent(planCode: PaidPlanCode): Promise<void> {
  const { error: metaErr } = await supabase.auth.updateUser({ data: { pending_plan: planCode } });
  if (metaErr) {
    console.warn('[planIntent] pending_plan update failed', metaErr.message);
  }

  if (!canRunOperationalBackgroundWork()) {
    return;
  }

  const cached = getOrganizationEntitlementsCacheIfFresh();
  const ent = cached !== undefined ? cached : await fetchMyOrganizationEntitlements();
  if (ent && ent.is_owner === true) {
    const { error: rpcErr } = await supabase.rpc('set_pending_subscription_plan', {
      p_plan_code: planCode,
    });
    if (rpcErr) {
      console.warn('[planIntent] set_pending_subscription_plan failed', rpcErr.message);
    }
  }
}

export async function clearPlanIntent(): Promise<void> {
  const { error: metaErr } = await supabase.auth.updateUser({ data: { pending_plan: null } });
  if (metaErr) {
    console.warn('[planIntent] pending_plan clear failed', metaErr.message);
  }

  const { error: rpcErr } = await supabase.rpc('clear_pending_subscription_plan');
  if (rpcErr) {
    console.warn('[planIntent] clear_pending_subscription_plan failed', rpcErr.message);
  }
  planIntentClearedForSession = true;
  clearMarketingPlanSession();
  setEphemeralPlanIntent(null);
}

/**
 * Limpia intención pendiente si ya no hace falta:
 * - suscripción de pago activa, o
 * - grant/trial activo (la prueba ya cumple la intención comercial).
 * Una sola vez por sesión para no pisar un checkout en curso.
 */
export async function clearPlanIntentIfSubscribed(
  entitlements:
    | {
        plan_code?: string | null;
        subscription_provider?: string | null;
        active_plan_grant?: unknown;
      }
    | string
    | null
    | undefined,
): Promise<void> {
  if (planIntentClearedForSession) return;

  const row =
    entitlements != null && typeof entitlements === 'object'
      ? entitlements
      : { plan_code: entitlements as string | null | undefined };

  if (parseActivePlanGrant(row.active_plan_grant)) {
    await clearPlanIntent();
    return;
  }

  if (!isPaidPlanActive(row.plan_code ?? null)) return;
  const provider = row.subscription_provider;
  if (
    provider == null ||
    String(provider).trim() === '' ||
    String(provider).toLowerCase() === 'null'
  ) {
    return;
  }
  await clearPlanIntent();
}

/** Resuelve plan pendiente: org DB > auth metadata. */
export function resolvePendingPlanIntent(
  entitlements: PlanEntitlementsRow | null | undefined,
  metadataPendingPlan: string | undefined
): PaidPlanCode | null {
  const fromOrg = normalizePaidPlanCode(entitlements?.pending_subscription_plan ?? null);
  if (fromOrg) return fromOrg;
  return normalizePaidPlanCode(metadataPendingPlan);
}
